import { z } from 'zod'

const ImportedPromptInputSchema = z.object({
  title: z.string().trim().min(1),
  userPrompt: z.string().trim().min(1),
})

type ImportedPromptInput = z.infer<typeof ImportedPromptInputSchema>

const ExportedPromptEntrySchema = z
  .object({
    label: z.string().trim().min(1),
    textContent: z.string().trim().min(1),
  })
  .transform(({ label, textContent }): ImportedPromptInput =>
    ImportedPromptInputSchema.parse({ title: label, userPrompt: textContent })
  )

export interface ImportPromptsSummary {
  importedCount: number
  failedCount: number
  failedPrompts: ImportedPromptInput[]
  isCancelled: boolean
}

function readPromptEntry(value: unknown): ImportedPromptInput | null {
  try {
    return ExportedPromptEntrySchema.parse(value)
  } catch {
    return null
  }
}

export function extractPromptsFromJson(rawContent: string): ImportedPromptInput[] {
  let parsed: unknown

  try {
    parsed = JSON.parse(rawContent) as unknown
  } catch {
    return []
  }

  if (!parsed || typeof parsed !== 'object') {
    return []
  }

  const promptsCollection = (parsed as Record<string, unknown>).prompts
  if (!Array.isArray(promptsCollection)) {
    return []
  }

  return promptsCollection
    .map((entry) => readPromptEntry(entry))
    .filter((prompt): prompt is ImportedPromptInput => prompt !== null)
}

interface ImportPromptsFromFilesParams {
  files: File[]
  createPrompt: (prompt: ImportedPromptInput) => Promise<void>
  signal?: AbortSignal
}

export async function importPromptsFromJsonFiles({
  files,
  createPrompt,
  signal,
}: ImportPromptsFromFilesParams): Promise<ImportPromptsSummary> {
  let importedCount = 0
  let failedCount = 0
  const failedPrompts: ImportedPromptInput[] = []

  for (const file of files) {
    if (signal?.aborted) {
      return { importedCount, failedCount, failedPrompts, isCancelled: true }
    }

    try {
      const fileContent = await file.text()

      if (signal?.aborted) {
        return { importedCount, failedCount, failedPrompts, isCancelled: true }
      }

      const prompts = extractPromptsFromJson(fileContent)

      if (prompts.length === 0) {
        failedCount += 1
        continue
      }

      for (const prompt of prompts) {
        if (signal?.aborted) {
          return { importedCount, failedCount, failedPrompts, isCancelled: true }
        }

        try {
          await createPrompt(prompt)
          importedCount += 1
        } catch {
          if (signal?.aborted) {
            return { importedCount, failedCount, failedPrompts, isCancelled: true }
          }

          failedCount += 1
          failedPrompts.push(prompt)
        }
      }
    } catch {
      failedCount += 1
    }
  }

  return { importedCount, failedCount, failedPrompts, isCancelled: false }
}
